/**
 * Every named part turns in place (TOM-587, a local change: see VENDORED.md).
 *
 * A page's CSS may turn, grow or slant any named part of a face:
 * `[data-part="value"]{transform:rotate(90deg)}`. An SVG element's transform
 * turns about its user space's origin unless it is told otherwise, and the
 * usual cure, `transform-box:fill-box;transform-origin:center`, turns text
 * about the wrong point in WebKit (Safari, and the iPhone's web view): the
 * value lands at the face's left edge and is clipped. Measured on
 * 2026-10-10 in Playwright's WebKit and on the iOS 27 simulator; fill-box
 * works there for shapes, never for text.
 *
 * So every named part carries its own visual centre as a `transform-origin`
 * presentation attribute, in its own user units. A presentation attribute
 * weighs nothing in the cascade, so any rule of a page's own that sets
 * transform-origin wins, and a plain transform turns the part about itself in
 * Chromium, WebKit and Firefox alike. The centre comes from the markup, so
 * the library draws the same thing in Node and in every browser:
 *
 * - text: x for text-anchor middle, or x plus or minus half an estimated
 *   width for start and end (0.55 of the font size a character, as the
 *   library sizes values), and 0.35 of the font size above the baseline;
 * - rect, circle, ellipse and line: their geometry;
 * - path, polyline and polygon: the box around every point they pass through,
 *   curves and arcs sampled along their length.
 *
 * A part that carries a `transform` attribute is left alone: the origin would
 * move it. None does (tests/parts.test.mjs).
 */

const round = (n) => {
  const r = Math.round(n * 100) / 100;
  return Object.is(r, -0) ? 0 : r;
};
const first = (value, fallback = 0) => {
  const n = parseFloat(String(value ?? "").trim());
  return Number.isFinite(n) ? n : fallback;
};

/** A box that grows to hold each point added. */
function box() {
  const b = {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
  };
  return {
    add(x, y) {
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      b.minX = Math.min(b.minX, x);
      b.minY = Math.min(b.minY, y);
      b.maxX = Math.max(b.maxX, x);
      b.maxY = Math.max(b.maxY, y);
    },
    centre() {
      return Number.isFinite(b.minX)
        ? [(b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2]
        : null;
    },
  };
}

const STEPS = 16;

/** Points along an SVG arc, from the endpoint form (SVG 2, appendix B.2.4). */
function arcPoints(x1, y1, rx, ry, degrees, large, sweep, x2, y2) {
  if (x1 === x2 && y1 === y2) return [];
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  if (!rx || !ry) return [[x2, y2]];
  const phi = (degrees * Math.PI) / 180,
    cos = Math.cos(phi),
    sin = Math.sin(phi);
  const dx = (x1 - x2) / 2,
    dy = (y1 - y2) / 2;
  const xp = cos * dx + sin * dy,
    yp = -sin * dx + cos * dy;
  const lambda = (xp * xp) / (rx * rx) + (yp * yp) / (ry * ry);
  if (lambda > 1) {
    rx *= Math.sqrt(lambda);
    ry *= Math.sqrt(lambda);
  }
  const num = rx * rx * ry * ry - rx * rx * yp * yp - ry * ry * xp * xp,
    den = rx * rx * yp * yp + ry * ry * xp * xp;
  let coef = den ? Math.sqrt(Math.max(0, num / den)) : 0;
  if (Boolean(large) === Boolean(sweep)) coef = -coef;
  const cxp = (coef * rx * yp) / ry,
    cyp = (-coef * ry * xp) / rx;
  const cx = cos * cxp - sin * cyp + (x1 + x2) / 2,
    cy = sin * cxp + cos * cyp + (y1 + y2) / 2;
  const angle = (ux, uy, vx, vy) =>
    Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
  const ux = (xp - cxp) / rx,
    uy = (yp - cyp) / ry,
    vx = (-xp - cxp) / rx,
    vy = (-yp - cyp) / ry;
  const start = angle(1, 0, ux, uy);
  let delta = angle(ux, uy, vx, vy);
  if (!sweep && delta > 0) delta -= 2 * Math.PI;
  else if (sweep && delta < 0) delta += 2 * Math.PI;
  const n = Math.max(4, Math.ceil(Math.abs(delta) / (Math.PI / 32)));
  const points = [];
  for (let k = 1; k <= n; k++) {
    const t = start + (delta * k) / n;
    points.push([
      cx + rx * Math.cos(t) * cos - ry * Math.sin(t) * sin,
      cy + rx * Math.cos(t) * sin + ry * Math.sin(t) * cos,
    ]);
  }
  return points;
}

const counts = { M: 2, L: 2, H: 1, V: 1, C: 6, S: 4, Q: 4, T: 2, A: 7, Z: 0 };

/** The centre of the box around everything a path's data passes through, or null. */
export function pathCentre(d) {
  const tokens =
    String(d ?? "").match(
      /[MmLlHhVvCcSsQqTtAaZz]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g,
    ) ?? [];
  const b = box();
  let i = 0,
    command = null,
    previous = null,
    x = 0,
    y = 0,
    startX = 0,
    startY = 0,
    // The last control point, for S and T.
    controlX = 0,
    controlY = 0;
  while (i < tokens.length) {
    if (/^[A-Za-z]$/.test(tokens[i])) command = tokens[i++];
    else if (command === null) break;
    const upper = command.toUpperCase(),
      relative = command !== upper,
      count = counts[upper];
    if (i + count > tokens.length) break;
    const n = tokens.slice(i, i + count).map(Number);
    if (n.some((v) => !Number.isFinite(v))) break;
    i += count;
    const ox = relative ? x : 0,
      oy = relative ? y : 0;
    const curve = (points) => {
      // A Bézier curve from (x, y) through its control points, sampled.
      const [p0, ...rest] = [[x, y], ...points];
      for (let k = 1; k <= STEPS; k++) {
        const t = k / STEPS;
        let layer = [p0, ...rest];
        while (layer.length > 1)
          layer = layer
            .slice(1)
            .map((p, j) => [
              layer[j][0] + (p[0] - layer[j][0]) * t,
              layer[j][1] + (p[1] - layer[j][1]) * t,
            ]);
        b.add(layer[0][0], layer[0][1]);
      }
    };
    let nextControl = null;
    switch (upper) {
      case "M":
        x = ox + n[0];
        y = oy + n[1];
        startX = x;
        startY = y;
        b.add(x, y);
        // Further pairs after a move are lines.
        command = relative ? "l" : "L";
        break;
      case "L":
        x = ox + n[0];
        y = oy + n[1];
        b.add(x, y);
        break;
      case "H":
        x = ox + n[0];
        b.add(x, y);
        break;
      case "V":
        y = oy + n[0];
        b.add(x, y);
        break;
      case "C": {
        const c2 = [ox + n[2], oy + n[3]],
          end = [ox + n[4], oy + n[5]];
        curve([[ox + n[0], oy + n[1]], c2, end]);
        [x, y] = end;
        nextControl = c2;
        break;
      }
      case "S": {
        const c1 = ["C", "S"].includes(previous)
            ? [2 * x - controlX, 2 * y - controlY]
            : [x, y],
          c2 = [ox + n[0], oy + n[1]],
          end = [ox + n[2], oy + n[3]];
        curve([c1, c2, end]);
        [x, y] = end;
        nextControl = c2;
        break;
      }
      case "Q": {
        const c = [ox + n[0], oy + n[1]],
          end = [ox + n[2], oy + n[3]];
        curve([c, end]);
        [x, y] = end;
        nextControl = c;
        break;
      }
      case "T": {
        const c = ["Q", "T"].includes(previous)
            ? [2 * x - controlX, 2 * y - controlY]
            : [x, y],
          end = [ox + n[0], oy + n[1]];
        curve([c, end]);
        [x, y] = end;
        nextControl = c;
        break;
      }
      case "A": {
        const end = [ox + n[5], oy + n[6]];
        for (const [px, py] of arcPoints(
          x,
          y,
          n[0],
          n[1],
          n[2],
          n[3],
          n[4],
          ...end,
        ))
          b.add(px, py);
        [x, y] = end;
        break;
      }
      case "Z":
        x = startX;
        y = startY;
        command = null;
        break;
    }
    if (nextControl) [controlX, controlY] = nextControl;
    previous = upper;
  }
  return b.centre();
}

const ENTITY = /&(?:amp|lt|gt|quot|apos|#\d+|#x[0-9a-f]+);/gi;
const ENTITIES = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" };

/**
 * A character's width in ems, near the system font's (SF on Apple devices,
 * which the faces draw in), as measured in Chromium and WebKit on 2026-10-10.
 * Small text is drawn wider, as SF Text is beside SF Display.
 */
function characterWidth(c, size) {
  if (/[0-9]/.test(c)) return size < 20 ? 0.65 : 0.6;
  if (/[.,:;'|!iIl]/.test(c)) return 0.3;
  if (c === " ") return 0.28;
  if (c === "%") return 0.92;
  if (c === "°") return 0.4;
  if (/[mwMW]/.test(c)) return 0.9;
  if (/[A-Z]/.test(c)) return 0.68;
  if (/[frtj]/.test(c)) return 0.35;
  if (/[a-z]/.test(c)) return 0.52;
  return 0.6;
}

/** A text's width, estimated from its characters, its font size and its letter spacing. */
export function textWidth(text, size, spacing = 0) {
  const characters = [...String(text)];
  return Math.max(
    0,
    characters.reduce((sum, c) => sum + characterWidth(c, size), 0) * size +
      Math.max(0, characters.length - 1) * spacing,
  );
}

/** A text's characters as drawn: its markup and entities read. */
function shownText(content) {
  return String(content)
    .replace(/<[^>]*>/g, "")
    .replace(ENTITY, (entity) => {
      const name = entity.slice(1, -1).toLowerCase();
      if (name.startsWith("#x"))
        return String.fromCodePoint(parseInt(name.slice(2), 16) || 32);
      if (name.startsWith("#"))
        return String.fromCodePoint(Number(name.slice(1)) || 32);
      return ENTITIES[name] ?? " ";
    });
}

/**
 * A text element's centre: x for text-anchor middle, or x plus or minus half
 * its estimated width for start and end; 0.35 of its font size above the
 * baseline, the middle of its figures and capitals.
 */
export function textCentre(attributes, content = "") {
  const size = first(attributes["font-size"], 16),
    x = first(attributes.x),
    y = first(attributes.y),
    anchor = String(attributes["text-anchor"] ?? "start").trim(),
    baseline = String(attributes["dominant-baseline"] ?? "").trim(),
    width =
      anchor === "middle"
        ? 0
        : textWidth(
            shownText(content),
            size,
            first(attributes["letter-spacing"]),
          );
  const cx =
    anchor === "middle" ? x : anchor === "end" ? x - width / 2 : x + width / 2;
  const cy = ["middle", "central"].includes(baseline) ? y : y - size * 0.35;
  return [cx, cy];
}

/** A named part's centre in its own user units, or null when it has none to give. */
export function partCentre(tag, attributes, content = "") {
  const a = attributes;
  switch (tag) {
    case "text":
      return textCentre(a, content);
    case "rect":
      return [
        first(a.x) + first(a.width) / 2,
        first(a.y) + first(a.height) / 2,
      ];
    case "circle":
    case "ellipse":
      return [first(a.cx), first(a.cy)];
    case "line":
      return [(first(a.x1) + first(a.x2)) / 2, (first(a.y1) + first(a.y2)) / 2];
    case "path":
      return pathCentre(a.d);
    case "polyline":
    case "polygon": {
      const n = (
        String(a.points ?? "").match(
          /[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g,
        ) ?? []
      ).map(Number);
      const b = box();
      for (let k = 0; k + 1 < n.length; k += 2) b.add(n[k], n[k + 1]);
      return b.centre();
    }
    default:
      return null;
  }
}

const ATTRIBUTE = /([A-Za-z_:][\w:.-]*)\s*=\s*"([^"]*)"/g;
/** A named part's start tag: only the tag is ever rewritten. */
const NAMED =
  /<(text|rect|circle|ellipse|line|path|polyline|polygon)\b((?:[^>"]|"[^"]*")*?\sdata-part="[^"]*"(?:[^>"]|"[^"]*")*?)(\/?)>/g;

/**
 * The face's markup with a `transform-origin` at the visual centre of every
 * named part that has none and carries no transform of its own. Only start
 * tags change; a text's content is read, never moved.
 */
export function withPartOrigins(svg) {
  const markup = String(svg);
  return markup.replace(NAMED, (whole, tag, attrs, selfClosing, offset) => {
    if (/\stransform(?:-origin)?\s*=/.test(attrs)) return whole;
    const attributes = {};
    for (const [, name, value] of attrs.matchAll(ATTRIBUTE))
      attributes[name] = value;
    let content = "";
    if (tag === "text" && !selfClosing) {
      const from = offset + whole.length,
        to = markup.indexOf("</text>", from);
      content = to < 0 ? "" : markup.slice(from, to);
    }
    const centre = partCentre(tag, attributes, content);
    if (!centre || !centre.every(Number.isFinite)) return whole;
    return `<${tag}${attrs.replace(/\s+$/, "")} transform-origin="${round(centre[0])} ${round(centre[1])}"${selfClosing}>`;
  });
}
